import { getAuth } from 'firebase/auth';
import WedifyApiRequest from '../../utils/wedify_api_request.js';

interface QuoteRequestFormData {
  firstName: string;
  lastName: string;
  weddingDate: string;
  isFlexible: boolean;
  guestCount: number;
  phone: string;
  vision: string;
}

interface QuoteRequestResponse {
  success: boolean;
  error?: string;
}

export const QuoteRequestAPI = {
  /**
   * Sends a quote request to a vendor
   * @param {QuoteRequestFormData} formData - The form data from the quote request
   * @param {string} vendorId - The ID of the vendor
   * @param {string} vendorEmail - The email of the vendor
   * @returns {Promise<QuoteRequestResponse>} Response indicating success or failure
   */
  sendQuoteRequest: async (
    formData: QuoteRequestFormData,
    vendorId: string,
    vendorEmail: string
  ): Promise<QuoteRequestResponse> => {
    try {
      // Format the wedding date if it exists
      const formattedDate = formData.weddingDate 
        ? new Date(formData.weddingDate).toLocaleDateString('en-US', {
            year: 'numeric', 
            month: 'long', 
            day: 'numeric'
          }) 
        : 'Not specified';
      
      // Generate email HTML
      const emailHtml = `<div style='font-family: sans-serif; padding: 24px; color: #333;'>\
        <h2 style='color: #FF4E8E;'>You've Got a New Wedding Lead! 🎉</h2>\
        <p>A couple is interested in working with you for their big day. Here's what they shared:</p>\
        <table style='margin: 16px 0; font-size: 14px;'>\
          <tr><td><strong>Couple:</strong></td><td>${formData.firstName} ${formData.lastName}</td></tr>\
          <tr><td><strong>Email:</strong></td><td>${getAuth().currentUser?.email}</td></tr>\
          <tr><td><strong>Phone:</strong></td><td>${formData.phone || 'Not provided'}</td></tr>\
          <tr><td><strong>Wedding Date:</strong></td><td>${formattedDate}${formData.isFlexible ? ' (Date is flexible)' : ''}</td></tr>\
          <tr><td><strong>Guest Count:</strong></td><td>${formData.guestCount}</td></tr>\
          <tr><td><strong>Message:</strong></td><td>${formData.vision}</td></tr>\
        </table>\
        <p><a href='https://wedify.be/vendor/inquiries/12345' \
        style='background-color: #5D5FEF; color: white; padding: 10px 16px; border-radius: 6px; text-decoration: none;'>\
        View Inquiry</a></p>\
        <hr style='margin-top: 2em; border: none; border-top: 1px solid #eee;'/>\
        <p style='font-size: 12px; color: #999;'>Sent by Wedify • <a href='https://wedify.be' style='color: #999;'>Visit Dashboard</a></p>\
      </div>`;
      
      // Get the current user's email and ID
      const email = getAuth().currentUser?.email;
      const userId = getAuth().currentUser?.uid;
      
      const wedifyApiRequest = new WedifyApiRequest();

      // Get user info
      const userInfo = await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/login/users/get/external/id?authService=firebase&externalClientId=${userId}`,
        "GET",
      );
      const userInfoJson = await userInfo?.json();

      // Prepare headers and body for the email request
      const headers = {
        'From-User-Id': userInfoJson?.id,
        'To-User-Id': vendorId,
        'From-User-Type': 'COUPLE',
        'To-User-Type': 'VENDOR'
      };

      const body = JSON.stringify({
        from: "admin@wedify.ai",
        to: [vendorEmail || "testwedifyai@gmail.com"],
        subject: `💍 New Wedding Inquiry from ${formData.firstName} & ${formData.lastName}!`,
        html: emailHtml,
        text: `You've Got a New Wedding Lead! 🎉
        A couple is interested in working with you for their big day. Here's what they shared:
        Couple: ${formData.firstName} ${formData.lastName}
        Email: ${getAuth().currentUser?.email}
        Phone: ${formData.phone || 'Not provided'}
        Wedding Date: ${formattedDate}${formData.isFlexible ? ' (Date is flexible)' : ''}
        Guest Count: ${formData.guestCount}
        Message: ${formData.vision}
        View Inquiry: https://wedify.be/vendor/inquiries/12345
        Sent by Wedify • Visit Dashboard: https://wedify.be`
      });

      // Send the email
      await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/resend/send`,
        "POST",
        body,
        headers
      );

      return { success: true };
    } catch (error) {
      console.error('Error sending quote request:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to send message. Please try again.' 
      };
    }
  }
}; 